import { COMMA, ENTER } from '@angular/cdk/keycodes';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  Inject,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  CrmDependencies,
  CrmInjectionToken,
  FieldValueInterface,
  FormInputComponent,
  ObjectType,
} from '../../../../tokens-and-interfaces';
import { InlineEditService } from '../../../../shared-components/model-driven-form/inline-edit.service';
import { SyncIconComponent } from '../../../../shared-components/model-driven-form/renderers/accounts/sync-icon/sync-icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { of, shareReplay, switchMap, tap } from 'rxjs';
import { Category, CategoryInterface, CategoryType } from '@vendasta/category';
import { BusinessCategoryModule, CategorySelectService } from '@galaxy/business-category';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { InfiniteScrollTriggerComponent } from '@vendasta/galaxy/infinite-scroll-trigger';
import { CategoryService } from '../../../../shared-services/category.service';
import { UpdaterService } from '../updater.service';

// account groups have 1 primary and 9 additional categories, so set to 10 total here so each can be synced over
const MAX_CATEGORIES = 10;

@Component({
  templateUrl: './categories-form-input.component.html',
  providers: [CategorySelectService],
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    GalaxyBadgeModule,
    SyncIconComponent,
    BusinessCategoryModule,
    InfiniteScrollTriggerComponent,
  ],
  styleUrls: ['../renderers.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesFormInputComponent extends FormInputComponent {
  @ViewChild('categoryInput') categoryInput!: ElementRef<HTMLInputElement>;

  override objectType: ObjectType = 'Company';
  _control: FormControl<string[]> = new FormControl<string[]>([], {
    nonNullable: true,
  });
  _group!: UntypedFormGroup;
  searchControl: FormControl<CategoryInterface | string> = new FormControl<CategoryInterface | string>('', {
    nonNullable: true,
  });
  separatorKeysCodes: number[] = [ENTER, COMMA];
  _label = '';

  protected readonly maxCategories = MAX_CATEGORIES;
  protected readonly hasMaxCategories = signal(this._control.value.length >= this.maxCategories);
  protected readonly exceedsMaxCategories = signal(this._control.value.length > this.maxCategories);
  protected isInputActive = false;
  private readonly locale$ = this.config.locale$;

  searchTerm = signal<CategoryInterface | string>('');
  selectedCategoriesIDs = signal<string[] | undefined>(undefined);
  selectedCategoriesIDs$ = toObservable(this.selectedCategoriesIDs);
  categoriesData$ = this.selectedCategoriesIDs$.pipe(
    switchMap((selectedCategories) => {
      if (selectedCategories === null) {
        return of([]);
      }
      if (!selectedCategories?.length) {
        this.loadingValues.set(false);
        this.hasCategoryData.set(true);
        return of([]);
      }
      return this.categoryService.getMultiCategories(selectedCategories).pipe(
        tap(() => {
          this.loadingValues.set(false);
          this.hasCategoryData.set(true);
        }),
      );
    }),
    shareReplay(1),
  );
  categoriesData = toSignal(this.categoriesData$);

  private hasCategoryData = signal(false);
  protected loadingNewValueId = signal('');

  set control(control: UntypedFormControl) {
    this._control = control as FormControl<string[]>;
    this._control.markAsPristine();
    this._control.setValue(this._control.value || []);
    this._control.clearValidators();
    this._control.addValidators(this.isCategoryValid.bind(this));
    this.selectedCategoriesIDs.set(this._control.value || []);
    this._control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((newValue) => {
        this.hasCategoryData.set(false);
        this.selectedCategoriesIDs.set(newValue || []);
        this.hasMaxCategories.set(newValue.length >= this.maxCategories);
        this.exceedsMaxCategories.set(newValue.length > this.maxCategories);
        this.changeDetRef.detectChanges();
      });
  }
  get control(): UntypedFormControl {
    return this._control;
  }

  private isCategoryValid(_: AbstractControl): ValidationErrors | null {
    if (!this.hasCategoryData()) {
      return this._control.errors;
    }
    const selectedCategories = this.selectedCategories();
    const hasMissingCategory = selectedCategories.some((category) => !category.categoryId);
    if (hasMissingCategory) {
      return {
        invalidCategory: true,
      };
    }
    return null;
  }

  set formGroup(group: UntypedFormGroup) {
    this._group = group;
  }

  get formGroup(): UntypedFormGroup {
    return this._group;
  }

  _fieldId = signal('');
  set fieldId(fieldId: string) {
    this._fieldId.set(fieldId);
  }

  get hasModification(): boolean {
    return this._control.dirty;
  }

  set label(label: string) {
    this._label = label;
  }

  get label(): string {
    return this._label;
  }

  get value(): FieldValueInterface {
    return {
      fieldId: this._fieldId(),
      stringValues: {
        values: this._control.value,
      },
    };
  }

  loadingValues = signal(true);
  availableOptions = toSignal(
    this.categorySelectService.categories$.pipe(
      map((categories) => categories.categories),
      startWith([] as Category[]),
    ),
  );
  selectedCategories = computed(() => {
    const selectedIds = this.selectedCategoriesIDs() ?? [];
    const categoryData = this.categoriesData();
    const selectedCategories = selectedIds.map((id) => {
      const category = categoryData?.find((option) => option?.externalId === id);
      if (category) {
        return category;
      }
      return { fullName: id, name: id, externalId: id };
    });
    return selectedCategories;
  });

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly destroyRef: DestroyRef,
    private readonly inlineEditService: InlineEditService,
    private readonly categorySelectService: CategorySelectService,
    private readonly categoryService: CategoryService,
    private readonly updaterService: UpdaterService,
    private changeDetRef: ChangeDetectorRef,
  ) {
    super();
    this.searchControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((newValue) => {
      this.searchTerm.set(newValue);
      if (typeof newValue === 'string') {
        this.categorySelectService.fetch(newValue);
      } else {
        this.categorySelectService.fetch(newValue?.fullName ?? '');
      }
    });
    this.categorySelectService.categoryType = CategoryType.CATEGORY_TYPE_V_CATEGORY;
    this.locale$
      ?.pipe(
        takeUntilDestroyed(),
        tap((locale) => this.categorySelectService.language$$.next(locale)),
      )
      .subscribe();

    effect(() => {
      this.selectedCategories();
      this.resetLoadingNewValueId();
      this._control.updateValueAndValidity();
    });

    // Listen for updates from UpdaterService for this field
    this.updaterService.updatedField
      .pipe(
        takeUntilDestroyed(),
        filter((fieldUpdate) => fieldUpdate.fieldId === this._fieldId()),
      )
      .subscribe((fieldUpdate) => {
        if (fieldUpdate.stringValues?.values) {
          console.log('Categories component received update from UpdaterService:', fieldUpdate.stringValues.values);
          this._control.patchValue(fieldUpdate.stringValues.values);
          this._control.markAsDirty();
        }
      });
  }

  private resetLoadingNewValueId() {
    if (this.hasCategoryData()) {
      this.loadingNewValueId.set('');
    }
  }

  displayFn(availableOptions: CategoryInterface[]): (category: CategoryInterface | string) => string {
    return (category: CategoryInterface | string): string => {
      if (!category) {
        return '';
      }
      if (typeof category === 'string') {
        const foundCategory = availableOptions.find((c) => c.externalId === category);
        return foundCategory?.fullName || category;
      }
      return category?.fullName ?? '';
    };
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const currentCategories = this._control.value;
    const hasCategory = currentCategories.some((c) => c === event.option.value);
    if (event.option.value && !hasCategory && !this.loadingNewValueId()) {
      this.loadingNewValueId.set(event.option.value);
      this._control.patchValue([...currentCategories, event.option.value]);
      this._control.markAsDirty();
    }
    this.categoryInput.nativeElement.value = '';
    this.searchControl.setValue('');
    this.categorySelectService.fetch('');
  }

  remove(externalId: string) {
    const currentCategories = this._control.value;
    const listWithoutSelectedCategory = currentCategories.filter((sp) => sp !== externalId);
    this._control.patchValue([...listWithoutSelectedCategory]);
    this._control.markAsDirty();
    this._control.updateValueAndValidity();
  }

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, [this.value]);
  }

  fetchMoreCategories(): void {
    this.categorySelectService.fetchNext();
  }
}
